// CandidateHome — the guided candidate landing ("Hem"/Översikt). A router for
// intent: shows the single most relevant next action first (profile → invite →
// onboarding → hired → find work), then supporting context.
//
// Action cards come from one ORDERED rule array fed to <HomeActionStack> — first
// matching rule = hero. Add/remove/reprioritise = edit the array. Job-search is
// the always-on baseline; notification-driven cards are a planned fast-follow.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, GraduationCap, Briefcase, FileText, PartyPopper, ArrowRight, Lightbulb } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCandidateProfile } from '@/contexts/CandidateProfileContext';
import { invitationApi } from '@/lib/api/invitationApi';
import { onboardingApi } from '@/lib/api/onboardingApi';
import { jobApplicationApi } from '@/lib/api/jobApplicationApi';
import type { Invitation } from '@/lib/models/invitation';
import type { EmployeeOnboarding } from '@/lib/models/onboarding';
import type { JobApplication } from '@/lib/models/jobApplication';
import { HomeActionStack, type HomeAction } from '@/components/custom-ui/HomeActionStack';
import { useNotificationActions } from '@/components/custom-ui/useNotificationActions';
import { ExplainerCard } from '@/components/custom-ui/ExplainerCard';
import { useInvitationsLauncher } from '@/contexts/InvitationsLauncherContext';
import { StatusBadge } from '@/components/custom-ui/status-badge';
import { EntityAvatar } from '@/components/custom-ui/EntityAvatar';
import { LoadingState } from '@/components/custom-ui/empty-state';
import { candidateProfileCompletion } from '@/lib/profileCompletion';
import CandidateProfileSummary from './CandidateProfileSummary';
import MyEngagementsCard from './MyEngagementsCard';
import { NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';

const CandidateHome: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { userProfile, profileDocuments } = useCandidateProfile();
    // Unread notifications surface as top-priority cards ("something happened").
    const notifActions = useNotificationActions();
    const { openInvitations } = useInvitationsLauncher();
    const [invites, setInvites] = useState<Invitation[]>([]);
    const [onboardings, setOnboardings] = useState<EmployeeOnboarding[]>([]);
    const [apps, setApps] = useState<JobApplication[]>([]);
    const [jobCount, setJobCount] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            invitationApi.getReceivedInvitations(),
            onboardingApi.getMyOnboarding(),
            jobApplicationApi.getMyApplications(1, 100),
            jobApplicationApi.listPublicNeeds({ page: 1, limit: 100 }),
        ]).then(([inv, onb, myApps, needs]) => {
            if (cancelled) return;
            setInvites(inv.invitations ?? []);
            setOnboardings(onb.onboardings ?? []);
            setApps(myApps.applications ?? []);
            setJobCount(needs.total ?? needs.needs?.length ?? 0);
        }).finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, []);

    const firstName = user?.first_name || 'där';
    const hasCv = profileDocuments.some((d) => d.document_type === 'cv');
    const { percent: pct, missing } = candidateProfileCompletion(userProfile, hasCv);
    const pendingInvites = invites.filter((i) => i.status === 'pending');
    const activeOnboarding = onboardings.filter((o) => o.status !== 'completed');
    const activeApps = apps.filter((a) => a.status !== 'withdrawn');
    const accepted = apps.find((a) => a.status === 'accepted');
    // Brand-new candidate: nothing going on yet → lead with the welcome copy.
    const isNew = pct < 100 && activeApps.length === 0 && pendingInvites.length === 0 && activeOnboarding.length === 0;

    if (loading) return <LoadingState message="Laddar..." />;

    // ── Action rules (ordered by priority; first match = hero) ──────────
    // Job-search is the always-on baseline, so no fallback is needed.
    const ruleActions: HomeAction[] = [
        {
            key: 'profile', when: !!userProfile && pct < 100, icon: User, tone: 'amber',
            title: `Slutför din profil (${pct}%)`,
            // #359 — was "En komplett profil krävs för att matcha och söka jobb",
            // which was untrue twice: nothing is required (you can apply with an
            // empty profile) and there is no matching engine at all. Promising a
            // gate that does not exist is worse than saying nothing, so this now
            // states the real benefit instead.
            description: missing.includes('CV')
                ? 'Ladda upp din CV — det är den arbetsgivare läser först.'
                : `Ju mer du fyller i, desto mer har arbetsgivaren att gå på. Saknas: ${missing.join(', ')}.`,
            ctaLabel: 'Slutför profil', ctaIcon: ArrowRight, onClick: () => navigate('/dashboard/profile'),
        },
        {
            key: 'invite', when: pendingInvites.length > 0, dedupeKey: 'invitation', icon: Mail, tone: 'brand',
            title: pendingInvites.length === 1 ? 'Du har en inbjudan' : `Du har ${pendingInvites.length} inbjudningar`,
            description: 'En arbetsgivare vill koppla dig till sin organisation.',
            ctaLabel: 'Visa inbjudningar', ctaIcon: ArrowRight, onClick: () => openInvitations(),
        },
        {
            key: 'onb', when: activeOnboarding.length > 0, dedupeKey: 'onboarding', icon: GraduationCap, tone: 'brand',
            title: 'Din onboarding väntar',
            description: 'Du har tilldelade moduler att slutföra.',
            ctaLabel: 'Till mina moduler', ctaIcon: ArrowRight, onClick: () => navigate('/dashboard/my-onboarding'),
        },
        {
            key: 'accepted', when: !!accepted, dedupeKey: 'workplace', icon: PartyPopper, tone: 'green',
            title: accepted?.need_role ? `Grattis — du blev anställd som ${accepted.need_role}` : 'Grattis — du blev anställd!',
            description: 'Välkommen ombord! Se din anställning och kommande onboarding.',
            ctaLabel: 'Min arbetsplats', ctaIcon: ArrowRight, onClick: () => navigate('/dashboard/my-workplace'),
        },
        {
            key: 'jobs', when: true, icon: Briefcase, tone: 'brand',
            title: jobCount > 0 ? `${jobCount} lediga tjänster` : 'Hitta lediga tjänster',
            description: 'Bläddra bland tjänster och skicka din ansökan.',
            ctaLabel: 'Sök jobb', ctaIcon: ArrowRight, onClick: () => navigate('/dashboard/jobs'),
        },
    ];

    // Notifications are additive: drop any whose concern a data card already
    // covers (the richer data card wins); prepend the rest as top priority.
    const activeKeys = new Set(ruleActions.filter((a) => a.when && a.dedupeKey).map((a) => a.dedupeKey));
    const notifs = notifActions.filter((g) => !g.dedupeKey || !activeKeys.has(g.dedupeKey));
    const actions: HomeAction[] = [...notifs, ...ruleActions];

    return (
        <div className="space-y-6">
            <ExplainerCard id="candidate-home" icon={Lightbulb} tone="amber" title="Så funkar startsidan">
                <p className="text-sm text-gray-600 mt-0.5">
                    Din startsida visar det viktigaste just nu — <strong>inbjudningar</strong>, <strong>onboarding</strong> som väntar och nya <strong>lediga tjänster</strong>. Åtgärda korten uppifrån och ner, och följ dina ansökningar längre ner.
                </p>
            </ExplainerCard>

            {/* Greeting spans full width so the columns below start level */}
            <div>
                <h2 className="text-xl font-bold text-gray-900">Hej {firstName}!</h2>
                <p className="text-sm text-gray-500">
                    {isNew ? 'Välkommen! Så kommer du igång:' : 'Här är vad som väntar på dig.'}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            {/* Left: actions + recent */}
            <div className="lg:col-span-2 space-y-6">
                <HomeActionStack actions={actions} />

                {activeApps.length > 0 && (
                    <div className={NEXILINK_CARD_WHITE_ELEVATED}>
                        <div className="flex items-center justify-between px-5 pt-5 pb-2">
                            <h3 className="text-lg font-semibold text-gray-900">Dina ansökningar</h3>
                            <button onClick={() => navigate('/dashboard/my-applications')} className="text-sm font-medium text-[#1A4D4F] hover:underline">
                                Visa alla
                            </button>
                        </div>
                        <div className="px-2 pb-2 divide-y divide-gray-50">
                            {activeApps.slice(0, 4).map((a) => (
                                <div key={a.id} className="flex items-center gap-3 px-3 py-3">
                                    <EntityAvatar icon={FileText} size="md" orgName={a.organisation_name} orgLogoUrl={a.organisation_logo_url} />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold text-gray-900 truncate">{a.need_role || a.need_title || 'Tjänst'}</p>
                                        <p className="text-xs text-gray-400 truncate">{a.organisation_name || ''}</p>
                                    </div>
                                    <StatusBadge status={a.status} />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Right rail: profile summary + engagements */}
            <div className="lg:col-span-1">
                <CandidateProfileSummary />
                <MyEngagementsCard />
            </div>
            </div>
        </div>
    );
};

export default CandidateHome;
